import { memo, useContext } from "react";
import PropTypes from "prop-types";
import ImageWithoutCarousel from "app/pages/SmartDP/Listing/SmartDPProduct/ImagesCarousel/ImageWithoutCarousel";
import ImagesCarouselPP from "app/pages/SmartDP/Listing/SmartDPProduct/ImagesCarousel/ImagesCarouselPP";
import ProductTopBar from "app/pages/Listing/Product/ProductTopBar/ProductTopBar";
import ProductDestinationTitleContainer from "app/pages/.shared/ProductDestinationTitle/ProductDestinationTitleContainer";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import ProductLinkContainer from "app/pages/.shared/ProductLink/ProductLinkContainer";
import "./ProductCard.scss";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import {
	BRANDS,
	FLAGSHIP_EVENT_KPIS,
	OFFER_CONTRACT_TYPES,
	PRODUCT_EXPERIENCES_WITH_HOTEL,
	PRODUCT_TAG_CODES,
	TOUR_PRODUCT_TYPES,
} from "app/constants";
import AppGlobalsContext from "app/AppGlobalsContext";
import ProductCardPricing from "app/pages/.shared/ProductCard/ProductCardPricing";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import { useLocation } from "react-router-dom";
import { shouldShowFavoriteBadge } from "app/utils/utils";
import Button, { BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import { getIsDubaiProduct, getUnbeatableOffer } from "app/utils/productUtils";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import ProductBadge from "app/features/product/components/ProductBadge/ProductBadge";
import { mapFicheProduitStoreToTag } from "app/utils/analytics-store-mapper";
import MilesEarnedContainer from "app/pages/.shared/MilesEarned/MilesEarnedContainer";
import { ArrowRightIcon } from "app/icons/navigation";

const IMG_SIZES = {
	small: { width: 768, height: 416 },
	medium: { width: 768, height: 416 },
	large: { width: 496, height: 282 },
	xlarge: { width: 496, height: 282 },
};

const ProductCard = ({
	isMobile,
	position,
	product = {},
	travelByTrainAvailable,
	displayCardSliderOnMob = true,
	isPriceRight = false,
	activeProductsFilters = false,
}) => {
	const { sendHits } = useFlagship();
	const { brand } = useContext(AppGlobalsContext);
	const { pathname } = useLocation();

	const hastripAdvisor = product.tripadvisor?.reviewsCount > 0;

	const shouldCropDestination = !isPriceRight || (isPriceRight && hastripAdvisor);
	const shouldCropProductName = !isPriceRight && hastripAdvisor;

	const productClassname = classNames({
		"product-card": true,
		"product-card--new-price-right": isPriceRight,
		"product-card--crop-destination": shouldCropDestination,
		"product-card--crop-name": shouldCropProductName,
	});

	const openInNewTab = !isMobile;

	const badgesToDisplay = product?.badges?.filter(
		badge => badge.code !== PRODUCT_TAG_CODES.COUP_DE_COEUR
	);

	const isDubaiProduct = getIsDubaiProduct(product?.geography);
	const showEmiratesPassBadge = brand === BRANDS.EK && isDubaiProduct;

	const isTourProduct = TOUR_PRODUCT_TYPES.includes(product?.productExperience);
	const unbeatableOfferTag = getUnbeatableOffer(product);
	const isFavorite = shouldShowFavoriteBadge(product);

	const allTags = [
		...(isTourProduct ? ["TOUR_PRODUCT"] : []),
		...(unbeatableOfferTag ? [unbeatableOfferTag] : []),
		...(isFavorite ? [PRODUCT_TAG_CODES.COUP_DE_COEUR] : []),
	];

	const { track } = useAnalytics();

	const handleProductClick = () => {
		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_PRODUCT_CARD,
			label: pathname,
		});

		track("view_offer", {
			position: position + 1,
			tags: allTags,
			product: mapFicheProduitStoreToTag(product),
			startingPrice: product?.fromPriceType?.value ?? 0,
			filters: activeProductsFilters,
		});
	};

	return (
		<div className={productClassname} data-testid="product">
			<div className="product-card__picture">
				{!displayCardSliderOnMob ? (
					<ImageWithoutCarousel
						handleProductClick={handleProductClick}
						product={product}
						openInNewTab={openInNewTab}
						isMobile={isMobile}
					/>
				) : (
					<ImagesCarouselPP
						sizes={IMG_SIZES}
						images={product?.photos}
						alt={product?.name}
						productUri={product?.uri}
						handleProductClick={handleProductClick}
						openInNewTab={openInNewTab}
					/>
				)}

				<div className="product-card__topbar">
					<ProductTopBar
						isFavorite={isFavorite}
						isTour={isTourProduct}
						{...(unbeatableOfferTag && { unbeatableOfferTag })}
					/>
				</div>
			</div>
			<div className="product-card__footer">
				<ProductLinkContainer
					handleProductClick={handleProductClick}
					productUri={product.uri}
					openOnNewTab={openInNewTab}
				>
					<div className="product-card__container">
						<div className="product-card__info product-card__info--left">
							<Typography
								variant={TYPOGRAPHY_VARIANTS.XL}
								variantMD={TYPOGRAPHY_VARIANTS.XL}
								isBold
							>
								<ProductDestinationTitleContainer
									country={product.country}
									region={product.region}
									resort={product.resort}
								/>
							</Typography>
							<Typography
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
								variantMD={TYPOGRAPHY_VARIANTS.REGULAR}
								isBold
							>
								<ProductNameWithCategory
									categoryType={product.categoryType}
									category={product.category}
									name={product.name}
								/>
							</Typography>

							<ProductRatingContainer
								tripAdvisor={product.tripadvisor || {}}
								holidayCheck={{
									...(product.holidaycheck || {}),
									size: "small",
								}}
								isCompact={isMobile}
							/>
						</div>

						<div className="product-card__info product-card__info--right">
							<ProductCardPricing
								travelByTrainAvailable={travelByTrainAvailable}
								isRental={product?.contractType === OFFER_CONTRACT_TYPES.RENTAL}
								includeOfferWithFlight={product?.includeOfferWithFlight}
								duration={product?.fromPriceType?.numberOfNights}
								publicPrice={product?.fromPriceType?.publicPrice}
								price={product?.fromPriceType?.price}
								hour={product?.expiration?.hour}
								day={product?.expiration?.day}
								type={product?.fromPriceType?.type}
								value={product?.fromPriceType?.value}
								isWithHotel={PRODUCT_EXPERIENCES_WITH_HOTEL.includes(
									product?.productExperience
								)}
							/>
							<div className="product-card__action">
								{isMobile ? (
									<Button
										variant={BUTTON_VARIANTS.FILLED}
										icon={<ArrowRightIcon />}
									/>
								) : (
									<Button
										variant={BUTTON_VARIANTS.FILLED}
										label={<FormattedMessage id="sdp.product.cta.label" />}
									/>
								)}
							</div>
						</div>
					</div>
					<MilesEarnedContainer price={product?.fromPriceType?.value} />
					{(badgesToDisplay?.length > 0 || showEmiratesPassBadge) && (
						<div className="product-card__badges-section">
							{showEmiratesPassBadge && (
								<ProductBadge
									label={<FormattedMessage id="product.badge.emirates.pass" />}
								/>
							)}
							{badgesToDisplay?.map((badge, id) => {
								return <ProductBadge key={id} label={badge?.label} />;
							})}
						</div>
					)}
				</ProductLinkContainer>
			</div>
		</div>
	);
};

ProductCard.propTypes = {
	isMobile: PropTypes.bool,
	position: PropTypes.number,
	product: PropTypes.object,
	travelByTrainAvailable: PropTypes.bool,
	displayCardSliderOnMob: PropTypes.bool,
	isPriceRight: PropTypes.bool,
	activeProductsFilters: PropTypes.array,
};

export default memo(ProductCard);
